import { StompSubscription } from '@stomp/stompjs';
import { ArtistI, NftAPI, NFTI } from '@tacans/shared-marketplace-layer';
import { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import withUniverseVerification from 'src/HOCs/withUniverseVerification';
import PageMeta from 'src/components/organisms/page-meta';
import { PAGES_LOCALES } from 'src/constants/common.constants';
import { constraints } from 'src/constants/constraints.constants';
import { useArtist } from 'src/features/common/artist/artist.hooks';
import { artistFetchThunk } from 'src/features/common/artist/ducks/artist.thunk';
import { categoryFetchThunk } from 'src/features/common/category/ducks/category';
import { NftPageLayout } from 'src/features/common/collectible/components/nft-page-layout';
import { NftPreviewMedia } from 'src/features/common/collectible/components/nft-previw-media';
import { NftAboutInfoContainer } from 'src/features/common/collectible/containers/nft-about-info-container';
import { NftActivitiesContainer } from 'src/features/common/collectible/containers/nft-activities-container';
import { NftActivitiesPage } from 'src/features/common/collectible/containers/nft-activities-page';
import { NftCollectionOwnerContainer } from 'src/features/common/collectible/containers/nft-collection-owner-container';
import { NftDetailsPriceContainer } from 'src/features/common/collectible/containers/nft-details-price-container';
import { NftMainInfoContainer } from 'src/features/common/collectible/containers/nft-main-info-container';
import { NftTransactionStatusContainer } from 'src/features/common/collectible/containers/nft-transaction-status-container';
import { actions as CollectibleActions } from 'src/features/common/collectible/ducks/collectible/collectible.slice';
import {
    fetchActiveLotsThunk,
    fetchActiveOwnersThunk,
    fetchAllLotsThunk,
    fetchNftCollectionThunk,
} from 'src/features/common/collectible/ducks/collectible/collectible.thunk';
import {
    useActiveLots,
    useCollectableClear,
    useCollectible,
    useCurrentBidsFetch,
    useFetchActiveLots,
    useFetchAllLots,
    useNftCollection,
    useNFTFetch,
} from 'src/features/common/collectible/hooks/collectible.hooks';
import { useFetchNftHistory } from 'src/features/common/collectible/hooks/use-nft-history';
import { useActiveOwners, useFetchNftActiveOwners } from 'src/features/common/collectible/hooks/use-nft-owners';
import { useTokenTransactionsListener } from 'src/features/common/collectible/hooks/useTokenTransactionsListener.hook';
import { INftTabsLocale } from 'src/features/common/collectible/types/locales';
import { useUniverse } from 'src/features/common/universe/hooks/use-universe';
import { fetchUsersByWalletsThunk } from 'src/features/common/user/ducks/user-list';
import { NftAboutInfoContainer as ONftAboutInfoContainer } from 'src/features/white-labels/ocean/collectible/containers/nft-about-info-container';
import { NftActivitiesPage as ONftActivitiesPage } from 'src/features/white-labels/ocean/collectible/containers/nft-activities-page';
import { NftCollectionOwnerContainer as ONftCollectionOwnerContainer } from 'src/features/white-labels/ocean/collectible/containers/nft-collection-owner-container';
import { NftDetailsPriceContainer as ONftDetailsPriceContainer } from 'src/features/white-labels/ocean/collectible/containers/nft-details-price-container';
import { NftMainInfoContainer as ONftMainInfoContainer } from 'src/features/white-labels/ocean/collectible/containers/nft-main-info-container';
import { NftMainInfoContainer as PHNftMainInfoContainer } from 'src/features/white-labels/phoenxholding/collectible/containers/nft-main-info-container';
import { NftMainInfoContainer as SYNftMainInfoContainer } from 'src/features/white-labels/syndicate/collectible/containers/nft-main-info-container';
import { useGetUniverseTemplate } from 'src/hooks/use-get-universe-template';
import { useMeta } from 'src/hooks/use-meta';
import { StompClient, StompMessage } from 'src/services/stomp';
import transactionsRegistry from 'src/services/transactions/transactions-registry';
import { ITransactionStatusE } from 'src/services/transactions/types';
import { wrapper } from 'src/store';
import { PageLayout } from 'src/ui/common/layouts/page-layout';
import { Tabs } from 'src/ui/common/molecules/tabs';
import { makeSSRApiRequest } from 'src/utils/api.utils';
import { decodeHTMLEntities } from 'src/utils/html.utils';
import { getNftTypeByStandard } from 'src/utils/lot.utils';

const NftLotsContainer = dynamic(
    () =>
        import('src/features/common/collectible/containers/nft-lots-container').then((d) => d.NftLotsContainer) as any,
    { ssr: false }
);

const PHNftLotsContainer = dynamic(
    () =>
        import('src/features/white-labels/phoenxholding/collectible/containers/nft-lots-container').then(
            (d) => d.NftLotsContainer
        ) as any,
    { ssr: false }
);

const NftLotsContainerOceanTemplate = dynamic(
    () =>
        import('src/features/white-labels/ocean/collectible/containers/nft-lots-container').then(
            (d) => d.NftLotsContainer
        ) as any,
    { ssr: false }
);

const NftOwnersContainer = dynamic(
    () =>
        import('src/features/common/collectible/containers/nft-owners-container').then(
            (d) => d.NftOwnersContainer
        ) as any,
    { ssr: false }
);

const NftPage = () => {
    const [selectedTab, setSelectedTab] = useState('activities');
    const { t } = useTranslation(['pages']);
    const subscription = useRef<StompSubscription | null>(null);

    const router = useRouter();
    const { projectName, domain } = useUniverse();
    const collectible = useCollectible();
    const artist = useArtist();
    const fetchActiveOwners = useFetchNftActiveOwners();
    const collectableFetch = useNFTFetch();
    const fetchActiveLots = useFetchActiveLots();
    const fetchAllLots = useFetchAllLots();
    const activeLots = useActiveLots();
    const activeOwners = useActiveOwners();
    const fetchCurrentBids = useCurrentBidsFetch();
    const collectibleClear = useCollectableClear();
    const collection = useNftCollection();
    const { isOceanTemplate, isPhoenxholding, isSyndicate } = useGetUniverseTemplate();
    const fetchNftHistory = useFetchNftHistory();

    useTokenTransactionsListener({ collectible: collectible! });

    const { isMultiple } = getNftTypeByStandard(collectible?.standard!);

    const { id } = router.query;

    const locale: INftTabsLocale = useMemo(() => t('nft.view.tabs', { returnObjects: true }), [t]);

    const getNftLotsContainer = () => {
        if (isPhoenxholding) {
            return <PHNftLotsContainer />;
        }
        return <NftLotsContainer />;
    };

    const tabs = useMemo(
        () =>
            [
                isMultiple && {
                    tabId: 'lots',
                    tab: `${locale.lots} (${activeLots.length})`,
                    component: getNftLotsContainer(),
                },
                { tabId: 'activities', tab: locale.activities, component: <NftActivitiesContainer /> },
                isMultiple && {
                    tabId: 'owners',
                    tab: `${locale.owners} (${activeOwners.totalCount})`,
                    component: <NftOwnersContainer />,
                },
            ].filter(Boolean) as { tabId: string; tab: string; component: any }[],
        [activeOwners.totalCount, isMultiple, locale, activeLots]
    );

    const tabsOceanTemplate = useMemo(
        () =>
            [
                isMultiple && {
                    tabId: 'lots',
                    tab: `${locale.lots} (${activeLots.length})`,
                    component: <NftLotsContainerOceanTemplate />,
                },
                { tabId: 'activities', tab: locale.activities, component: <NftActivitiesContainer /> },
                isMultiple && {
                    tabId: 'owners',
                    tab: `${locale.owners} (${activeOwners.totalCount})`,
                    component: <NftOwnersContainer />,
                },
            ].filter(Boolean) as { tabId: string; tab: string; component: any }[],
        [activeOwners.totalCount, isMultiple, locale, activeLots]
    );

    const title = useMemo(() => {
        const pageTitle = `${collection?.name ? ` — ${collection?.name}` : ''} | ${projectName} `;
        return decodeHTMLEntities(`${collectible?.name!}${pageTitle}`);
    }, [collection?.name, projectName, collectible?.name]);

    const meta = useMeta('collectible', {
        projectName,
        projectUrl: domain!,
        collectibleName: collectible?.name!,
    });

    const handleGoToLots = useCallback(async () => {
        setSelectedTab('lots');
        document.querySelector('[data-component-id="tab-item-lots"]')?.scrollIntoView();
    }, []);

    const fetchData = useCallback(async () => {
        if (id) {
            await collectableFetch(id as string);
            await fetchActiveLots(id as string);
            await fetchAllLots(id as string);
            await fetchActiveOwners(id as string, {
                page: 0,
                limit: constraints.collectible.multiple.editions.max,
            });
            await fetchNftHistory(id as string, {
                limit: 10,
                page: 0,
            });
        }
    }, [id, collectableFetch, fetchActiveLots, fetchAllLots, fetchActiveOwners]);

    const stompCallback = useCallback(
        (message: StompMessage) => {
            fetchData();
            try {
                const { nft_id: tokenId, transaction_hash: trHash } = JSON.parse(message.body);
                if (trHash) {
                    transactionsRegistry.updateStatusByTrHash(trHash, ITransactionStatusE.Success);
                } else {
                    transactionsRegistry.updateStatusByTokenId(String(tokenId), ITransactionStatusE.Success);
                }
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('Cant parse msg body', message, err);
            }
        },
        [fetchData]
    );

    useEffect(() => {
        if (collectible?.tokenId && !subscription.current) {
            StompClient.subscribe(`nft.${collectible.tokenId}`, stompCallback)
                .then((sub) => {
                    subscription.current = sub;
                    // eslint-disable-next-line no-console
                })
                .catch((reason) => console.warn(reason));
        }
    }, [collectible, stompCallback]);

    useEffect(() => {
        fetchCurrentBids(activeLots.map((lot) => lot.id));
    }, [activeLots, fetchCurrentBids]);

    useEffect(
        () => () => {
            subscription.current?.unsubscribe();
        },
        []
    );

    // eslint-disable-next-line
    useEffect(() => () => collectibleClear(), []);

    useEffect(() => {
        if (isMultiple) {
            setSelectedTab('lots');
        }
    }, [isMultiple]);

    const getNftCollectionOwnerContainer = useCallback(
        (collectible: NFTI, artist: ArtistI) => {
            if (isOceanTemplate) {
                return <ONftCollectionOwnerContainer nft={collectible} artist={artist} />;
            }
            return <NftCollectionOwnerContainer nft={collectible} artist={artist} />;
        },
        [isOceanTemplate]
    );

    const getNftMainInfoContainer = useCallback(
        (handleGoToLots) => {
            if (isOceanTemplate) {
                return <ONftMainInfoContainer onGoToLots={handleGoToLots} />;
            }
            if (isPhoenxholding) {
                return <PHNftMainInfoContainer onGoToLots={handleGoToLots} />;
            }
            if (isSyndicate) {
                return <SYNftMainInfoContainer onGoToLots={handleGoToLots} />;
            }
            return <NftMainInfoContainer onGoToLots={handleGoToLots} />;
        },
        [isOceanTemplate, isPhoenxholding]
    );

    const getNftAboutContainer = useCallback(() => {
        if (isOceanTemplate) {
            return <ONftAboutInfoContainer />;
        }

        return <NftAboutInfoContainer />;
    }, [isOceanTemplate]);

    const getDetailsPriceContainer = useCallback(
        (artist) => {
            if (isOceanTemplate) {
                return <ONftDetailsPriceContainer artist={artist} />;
            }

            return <NftDetailsPriceContainer artist={artist} />;
        },
        [isOceanTemplate]
    );

    const getActivitiesPage = useCallback(() => {
        if (isOceanTemplate) {
            return <ONftActivitiesPage selectedTab={selectedTab} tabs={tabsOceanTemplate} />;
        }
        return <NftActivitiesPage selectedTab={selectedTab} tabs={tabs} />;
    }, [isOceanTemplate, selectedTab, activeLots]);

    return (
        <>
            <PageMeta
                title={title}
                description={meta.description}
                ogImage={collectible?.previewAssets?.webps?.largeUrl ?? collectible?.previewAssets?.originalUrl!}
                resizeOgImage
            />
            <NftTransactionStatusContainer nftId={id as string} />
            <PageLayout fullWidth={false}>
                <NftPageLayout>
                    <NftPageLayout.LeftPanel>
                        {collectible && <NftPreviewMedia nft={collectible} />}
                        {collectible && getNftCollectionOwnerContainer(collectible, artist)}
                    </NftPageLayout.LeftPanel>
                    <NftPageLayout.DetailsPanel>
                        {getNftMainInfoContainer(handleGoToLots)}
                        {getNftAboutContainer()}
                        {getDetailsPriceContainer(artist)}
                    </NftPageLayout.DetailsPanel>
                    <NftPageLayout.TabsPanel>
                        <Tabs selectedTabId={selectedTab} handleSelectTab={setSelectedTab}>
                            {getActivitiesPage()}
                        </Tabs>
                    </NftPageLayout.TabsPanel>
                </NftPageLayout>
            </PageLayout>
        </>
    );
};

export const getServerSideProps: GetServerSideProps = wrapper.getServerSideProps((store) => async (ctx) => {
    const { id } = ctx.query;
    const props = {
        ...(await serverSideTranslations(ctx.locale as string, PAGES_LOCALES)),
    };

    if (!id || isNaN(+id)) {
        return { props, notFound: true };
    }

    try {
        const res = await makeSSRApiRequest(
            {
                req: ctx.req,
                res: ctx.res,
                getAccess: true,
            },
            ({ access }) =>
                NftAPI.fetchById(
                    id as string,
                    access
                        ? {
                              Authorization: access,
                          }
                        : {}
                )
        );

        const collectible = res.data.nft;

        if (!collectible) throw new Error('404');

        // @ts-ignore
        await Promise.all([
            store.dispatch(CollectibleActions.setCollectible(collectible)),
            // @ts-ignore
            store.dispatch(fetchActiveLotsThunk(id as string)),
            // @ts-ignore
            store.dispatch(categoryFetchThunk()),
            // @ts-ignore
            collectible.collectionId && store.dispatch(fetchNftCollectionThunk(collectible.collectionId)),
            // @ts-ignore
            collectible.artistId && store.dispatch(artistFetchThunk(collectible.artistId)),
            // @ts-ignore
            store.dispatch(fetchAllLotsThunk(id as string)),
            // @ts-ignore
            store.dispatch(fetchActiveOwnersThunk({ nftId: id as string })),
            // @ts-ignore
            store.dispatch(
                // @ts-ignore
                fetchUsersByWalletsThunk([
                    {
                        address: collectible.author,
                        blockchainId: collectible.blockchainId,
                    },
                    {
                        address: collectible.owner,
                        blockchainId: collectible.blockchainId,
                    },
                ])
            ),
        ]);
    } catch (err: any) {
        return { props, notFound: true };
    }

    return { props };
});

export default withUniverseVerification(NftPage);